import { PokemonType } from "types/battle-engine/battle";
import { PixelArt } from "@/components/cards/item-pixel-art";
import { useDominantColors } from "@/hooks/use-dominant-colors";
import {
  Backdrop,
  cardBackground,
  LightStripes,
  FULL_ART_BG_URL,
  TALL_CARD_CLASS,
  Scrim,
  TiltCard,
} from "@/components/cards/card-primitives";
import { TYPE_COLORS, themeForItem } from "@/components/cards/card-theme";
import { FoilOverlay, GlareLayer } from "@/components/cards/foil/foil-layers";
import { cleanItemDescription } from "@/components/cards/utils/clean-item-description";
import type { FoilConfig } from "@/components/cards/foil/foil-config";
import type { ItemCardDefinition } from "types/item-cards/item-card-definition";

const EnergyPips = ({ def, size = 13 }: { def: ItemCardDefinition; size?: number }) => {
  const pips = Object.entries(def.energyCost).flatMap(([type, count]) =>
    Array.from({ length: count ?? 0 }, (_, i) => ({ type, id: `${type}-${i}` })),
  );
  if (pips.length === 0) return null;

  return (
    <div className="flex items-center" style={{ gap: 2 }}>
      {pips.map(({ type, id }) => (
        <span
          key={id}
          className="flex justify-center items-center border border-black/80 rounded-full"
          style={{
            width: size,
            height: size,
            backgroundColor: TYPE_COLORS[type as PokemonType] ?? "#a8a878",
            boxShadow: "0 1px 2px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.4)",
          }}
        >
          <img
            src={`/assets/pokemon-types/${type.toLowerCase()}.svg`}
            alt={type}
            style={{ width: size * 0.65, height: size * 0.65 }}
          />
        </span>
      ))}
    </div>
  );
};

/**
 * Item card: type-themed backdrop, crisp pixel icon, description, energy pips.
 * Pass `fullArt` to enable the layered full-art vibe (cosmic backdrop → big
 * blurred icon → art-matched light stripes) behind the icon.
 */
export const ItemCard = ({
  def,
  iconUrl,
  fullArt = false,
  foil,
}: {
  def: ItemCardDefinition;
  /** Item icon URL (pixel art). */
  iconUrl: string;
  /** Enables the blurred full-art backdrop behind the icon. */
  fullArt?: boolean;
  foil?: FoilConfig | boolean;
}) => {
  const t = themeForItem(def);
  const stripeColors = useDominantColors(fullArt ? iconUrl : "", 3);

  return (
    <TiltCard>
      <div className={TALL_CARD_CLASS} style={cardBackground(t)}>
        {fullArt && (
          <>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${FULL_ART_BG_URL})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: 0.55,
                mixBlendMode: "screen",
              }}
            />

            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url(${iconUrl})`,
                backgroundSize: "260%",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                imageRendering: "pixelated",
                filter: "blur(4px) saturate(1.5)",
                opacity: 0.45,
              }}
            />

            <LightStripes colors={stripeColors} />

            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(180deg, ${t.dark}bb 0%, transparent 40%, ${t.dark}dd 100%)`,
              }}
            />
          </>
        )}
        <Backdrop t={t} />

        <div className="z-10 relative flex flex-col items-center pt-3">
          <span
            className="font-black text-[13px] text-white uppercase tracking-[0.18em]"
            style={{ textShadow: "0 2px 4px rgba(0,0,0,0.8)" }}
          >
            {def.name}
          </span>
          <div
            className="mt-1 rounded-full w-10 h-[2px]"
            style={{ background: `linear-gradient(90deg, transparent, ${t.light}, transparent)` }}
          />
        </div>

        <div className="z-10 relative flex justify-center items-center mt-4 h-[100px]">
          <div
            className="absolute rounded-[50%] w-16 h-4"
            style={{ background: "radial-gradient(ellipse, rgba(0,0,0,0.55) 0%, transparent 70%)" }}
          />
          <div
            className="absolute rounded-full w-24 h-24 blur-md"
            style={{ background: `radial-gradient(circle, ${t.light}55 0%, transparent 70%)` }}
          />
          <PixelArt src={iconUrl} alt={def.name} size={104} className="z-10 relative" />
        </div>

        <Scrim>
          <p className="text-[9px] text-center text-white/90 leading-[11px] line-clamp-3">
            {cleanItemDescription(def.description)}
          </p>
          <div className="flex justify-between items-center mt-1.5">
            <EnergyPips def={def} />
            <span className="font-bold text-[8px] tracking-[0.2em]" style={{ color: t.light }}>
              {t.tag}
            </span>
          </div>
        </Scrim>

        <FoilOverlay foil={foil} seed={def.key} />
        <GlareLayer />
      </div>
    </TiltCard>
  );
};
